import { roundToCents } from 'utils/roundToCents'

/**
 * The rules that decide the interest on a payoff PAYMENT, kept out of the modal so they can be
 * read and tested on their own: the days it bills, the per-diem those days earn, and how much of
 * it a payment allocates. They exist because a payoff is quoted to its own date rather than to the
 * schedule, and a loan can be paid ahead of that date.
 */

/**
 * Days of note interest a payoff collects: from the Interest Paid Thru date to the payoff date,
 * plus the payoff date itself when the servicer bills "through" it rather than "to" it (Servicing
 * Information > Interest Calculations, falling back to the company-wide rule, resolved on the
 * server and sent with the fill data as `payoffInterestAccrualEnd`).
 *
 * The count is SIGNED on purpose. A loan paid ahead of its payoff date has a paid-thru date after
 * it, so the window runs backwards: the borrower prepaid that interest and is owed it back. The
 * payoff demand states exactly that, as an "Interest Credit From <payoff date> To <paid thru>"
 * line, and the prior servicer quotes it the same way, so clamping the count at zero made the
 * payment collect the credit from the borrower instead of returning it (longhorn 54101: 19 days on
 * a $205,000 balance at 12.99%, $1,405.45 the payment kept and the demand gave back).
 *
 * Returns 0 when either date is missing: with nothing to measure from there is no per-diem, which
 * is not the same as a credit.
 */
export const payoffInterestDays = (
  paidThruDate: string | undefined,
  payoffDate: string | undefined,
  accrualEnd: string | undefined,
): number => {
  if (!paidThruDate || !payoffDate) return 0
  const throughPayoffDate = accrualEnd === 'through' ? 1 : 0
  return Math.round((new Date(payoffDate).getTime() - new Date(paidThruDate).getTime()) / 86400000) + throughPayoffDate
}

/**
 * How much of the interest owed a payment allocates: up to what is owed, and up to what the funds
 * applied actually cover. The rest becomes an Unpaid Interest receivable (or is capitalized), which
 * is the caller's business.
 *
 * A NEGATIVE figure is the prepaid-interest credit above. It is neither capped by the cash on hand
 * nor floored away: it is money owed back, so there is no "available" to measure it against, and
 * flooring it dropped the credit the payoff prefill had just quoted the moment anything re-split
 * the payment, leaving Principal to absorb it.
 */
export const allocatableInterest = (interestOwed: number, available: number): number =>
  interestOwed < 0 ? interestOwed : Math.max(0, Math.min(interestOwed, available))

/**
 * The per-diem note interest a payoff collects: the base it accrues on (principal plus the unpaid
 * balances the loan charges interest on) times the note rate, over the day count above, on the
 * loan's day-count basis. One numerator and then the divisions, which is the order the backend
 * payoff quote uses, so the demand and the payment that settles it cannot drift.
 *
 * Rounded with `roundToCents` and NOT `Number(x.toFixed(2))`, which is what this screen did and
 * what left it quoting a cent under the demand it was settling. A balance times a rate over a day
 * count lands on an exact half cent constantly - it is a third, a sixth or a ninth of a cent far
 * more often than it is even - and `toFixed` rounds the binary double, which for a decimal ending
 * in half a cent almost never sits exactly on the midpoint. $165,000 at 10.49% actual/360 for the
 * 18 days from a 10/01 paid-thru date is exactly $865.425, held as 865.42499999999995, so `toFixed`
 * quoted $865.42 where the prior servicer and the payoff demand both say $865.43 (longhorn loan
 * 49857 / TMO 313597, row 461: the demand was fixed server-side on 10/19 and this screen, which
 * quotes its own per-diem rather than reading the demand's, was not).
 *
 * Over a sweep of 1.32M realistic balance/rate/day-count/basis combinations, 25,123 come to an
 * exact half cent and `toFixed` quoted 10,734 of them a penny light.
 *
 * The figure is SIGNED, following the day count: a loan paid ahead of its payoff date is owed the
 * prepaid interest back, and rounding half away from zero credits that cent back rather than
 * keeping it.
 */
export const payoffPerDiem = (perDiemBase: number, rate: number, days: number, denom: number): number =>
  roundToCents((perDiemBase * rate * days) / 100 / denom)
